import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useUser } from '../context/UserContext';
import formatDateTime from '../helpers/formatDateTime';
import './tasks.css';

const PRIORITIES = [
    { key: 'high', label: 'High' },
    { key: 'medium', label: 'Medium' },
    { key: 'low', label: 'Low' }
];

const DEFAULT_DEADLINE_TIME = '16:00';

const TASK_FUNCTIONS = [
    { key: 'finance', label: 'Finance' },
    { key: 'cleaning', label: 'Cleaning' },
    { key: 'support', label: 'Support' },
    { key: 'upgrades', label: 'Upgrades' },
    { key: 'expansions', label: 'Expansions' }
];

const priorityKey = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'urgent') return 'high';
    if (normalized === 'normal') return 'medium';
    return PRIORITIES.some((priority) => priority.key === normalized) ? normalized : 'medium';
};

const isDone = (task) => ['done', 'completed'].includes(String(task.status || '').toLowerCase());

const taskFunctionKey = (value) => {
    const normalized = String(value || 'support').toLowerCase();
    return TASK_FUNCTIONS.some((taskFunction) => taskFunction.key === normalized) ? normalized : 'support';
};

const taskGroupKey = (task, grouping) => {
    if (grouping === 'assignee') return task.assigneeUserId ? `assignee:${task.assigneeUserId}` : 'assignee:none';
    if (grouping === 'function') return `function:${taskFunctionKey(task.taskFunction)}`;
    return task.propertyId == null ? 'personal' : `property:${task.propertyId}`;
};

const displayName = (person) => person?.display || person?.email || 'Unassigned';

// Golden-angle hues keep consecutive user ids visually distinct and identical for every viewer.
const assigneeColorStyle = (userId) => {
    const id = Number(userId);
    if (!Number.isInteger(id) || id <= 0) return { '--assignee-color': '#8a9692', '--assignee-tint': '#f6f7f6' };
    const hue = Math.round((id * 137.508) % 360);
    return { '--assignee-color': `hsl(${hue} 52% 38%)`, '--assignee-tint': `hsl(${hue} 60% 96%)` };
};

const toDateTimeInput = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const TaskCard = ({ task, properties, canDrag, groupKey, onUpdate, onDrop, onComment, onLike }) => {
    const [commentsOpen, setCommentsOpen] = useState(false);
    const [comment, setComment] = useState('');
    const [commentBusy, setCommentBusy] = useState(false);
    const [replyThreadId, setReplyThreadId] = useState(null);
    const [replyDraft, setReplyDraft] = useState('');
    const [replyBusy, setReplyBusy] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editBusy, setEditBusy] = useState(false);
    const [titleDraft, setTitleDraft] = useState(task.title);
    const [descriptionDraft, setDescriptionDraft] = useState(task.description || '');
    const [deadlineDraft, setDeadlineDraft] = useState(toDateTimeInput(task.deadline || task.dueAt));
    const property = task.propertyId == null
        ? null
        : properties.find((item) => Number(item.id) === Number(task.propertyId));
    const assignees = property?.hosts || [];
    const done = isDone(task);

    useEffect(() => {
        setDeadlineDraft(toDateTimeInput(task.deadline || task.dueAt));
    }, [task.deadline, task.dueAt]);

    const submitComment = async (event) => {
        event.preventDefault();
        if (!comment.trim()) return;
        setCommentBusy(true);
        const succeeded = await onComment(task.id, comment.trim());
        if (succeeded) setComment('');
        setCommentBusy(false);
    };

    const startReply = (entry, threadId) => {
        setReplyThreadId(threadId);
        setReplyDraft(entry.id === threadId ? '' : `@${displayName(entry.author)} `);
    };

    const submitReply = async (event) => {
        event.preventDefault();
        if (!replyDraft.trim()) return;
        setReplyBusy(true);
        const succeeded = await onComment(task.id, replyDraft.trim(), replyThreadId);
        if (succeeded) {
            setReplyThreadId(null);
            setReplyDraft('');
        }
        setReplyBusy(false);
    };

    const allComments = task.comments || [];
    const topLevelComments = allComments.filter((entry) => !entry.parentCommentId);
    const repliesFor = (commentId) => allComments.filter((entry) => Number(entry.parentCommentId) === Number(commentId));

    const renderComment = (entry, threadId) => {
        const likeLabel = `${entry.likedByMe ? 'Liked' : 'Like'}${entry.likeCount > 0 ? ` · ${entry.likeCount}` : ''}`;
        const likedByTitle = entry.likedBy?.length ? `Liked by ${entry.likedBy.join(', ')}` : undefined;
        return (
            <div className="task-comment" key={entry.id}>
                <div><strong>{displayName(entry.author)}</strong><time>{formatDateTime(entry.createdAt)}</time></div>
                <p>{entry.body}</p>
                <div className="task-comment-actions">
                    {task.canWrite ? (
                        <button
                            type="button"
                            className={`task-comment-action${entry.likedByMe ? ' task-comment-liked' : ''}`}
                            aria-pressed={Boolean(entry.likedByMe)}
                            title={likedByTitle}
                            onClick={() => onLike(task.id, entry.id, !entry.likedByMe)}
                        >
                            {likeLabel}
                        </button>
                    ) : entry.likeCount > 0 && (
                        <span className="task-comment-like-count" title={likedByTitle}>
                            {entry.likeCount} {entry.likeCount === 1 ? 'like' : 'likes'}
                        </span>
                    )}
                    {task.canWrite && (
                        <button type="button" className="task-comment-action" onClick={() => startReply(entry, threadId)}>Reply</button>
                    )}
                </div>
            </div>
        );
    };

    const submitTaskEdit = async (event) => {
        event.preventDefault();
        const title = titleDraft.trim();
        if (!title) return;
        setEditBusy(true);
        const saved = await onUpdate(task.id, { title, description: descriptionDraft.trim() });
        if (saved) setEditing(false);
        setEditBusy(false);
    };

    return (
        <article
            className={`task-card${task.canWrite ? '' : ' task-card-readonly'}`}
            style={assigneeColorStyle(task.assigneeUserId)}
            draggable={canDrag && task.canWrite && !done}
            onDragStart={(event) => {
                event.dataTransfer.setData('text/plain', String(task.id));
                event.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(event) => {
                if (canDrag && task.canWrite && !done) event.preventDefault();
            }}
            onDrop={(event) => {
                if (!canDrag || !task.canWrite || done) return;
                event.preventDefault();
                onDrop(event.dataTransfer.getData('text/plain'), task.id, priorityKey(task.priority), groupKey);
            }}
        >
            <div className="task-card-heading">
                <div>
                    <p className="task-property-name">{task.propertyId == null ? 'Personal task' : property?.name || 'Property'}</p>
                    {!editing && <h3>{task.title}</h3>}
                </div>
                <span className={`task-priority task-priority-${priorityKey(task.priority)}`}>
                    {priorityKey(task.priority)}
                </span>
            </div>
            {editing && (
                <form className="task-edit-form" onSubmit={submitTaskEdit}>
                    <label>
                        Task
                        <input autoFocus required maxLength={255} value={titleDraft} onChange={(event) => setTitleDraft(event.target.value)} />
                    </label>
                    <label>
                        Details
                        <textarea rows="3" maxLength={4000} value={descriptionDraft} onChange={(event) => setDescriptionDraft(event.target.value)} />
                    </label>
                    <div className="task-edit-actions">
                        <button type="button" onClick={() => { setTitleDraft(task.title); setDescriptionDraft(task.description || ''); setEditing(false); }}>Cancel</button>
                        <button type="submit" disabled={editBusy || !titleDraft.trim()}>{editBusy ? 'Saving...' : 'Save changes'}</button>
                    </div>
                </form>
            )}
            {!editing && task.description && <p className="task-description">{task.description}</p>}
            {task.canWrite ? (
                <label className="task-deadline-control">
                    <span>Deadline</span>
                    <input
                        type="datetime-local"
                        aria-label={`Deadline for ${task.title}`}
                        value={deadlineDraft}
                        onChange={(event) => setDeadlineDraft(event.target.value)}
                        onBlur={() => {
                            if (deadlineDraft !== toDateTimeInput(task.deadline || task.dueAt)) {
                                onUpdate(task.id, { deadline: deadlineDraft ? new Date(deadlineDraft).toISOString() : null });
                            }
                        }}
                    />
                </label>
            ) : (task.deadline || task.dueAt) ? (
                <p className="task-deadline-readonly">Deadline: {formatDateTime(task.deadline || task.dueAt)}</p>
            ) : null}

            <div className="task-card-assignment">
                <span className="task-assignee-mark" aria-hidden="true">
                    {(displayName(task.assignee).charAt(0) || '?').toUpperCase()}
                </span>
                {task.canWrite && task.propertyId != null ? (
                    <select
                        aria-label={`Assignee for ${task.title}`}
                        value={task.assigneeUserId || ''}
                        onChange={(event) => onUpdate(task.id, { assigneeUserId: event.target.value || null })}
                    >
                        <option value="">Unassigned</option>
                        {assignees.map((person) => (
                            <option key={person.id} value={person.id}>{displayName(person)}</option>
                        ))}
                    </select>
                ) : <strong>{displayName(task.assignee)}</strong>}
                <span className="task-creator">Created by {displayName(task.creator)}</span>
            </div>

            <div className="task-card-actions">
                {task.canWrite && (
                    <>
                        {!editing && <button type="button" className="task-edit-button" onClick={() => { setTitleDraft(task.title); setDescriptionDraft(task.description || ''); setEditing(true); }}>Edit</button>}
                        <label className="task-function-control">
                            <span>Function</span>
                            <select
                                aria-label={`Function for ${task.title}`}
                                value={taskFunctionKey(task.taskFunction)}
                                onChange={(event) => onUpdate(task.id, { taskFunction: event.target.value })}
                            >
                                {TASK_FUNCTIONS.map((taskFunction) => <option key={taskFunction.key} value={taskFunction.key}>{taskFunction.label}</option>)}
                            </select>
                        </label>
                        <label className="task-priority-control">
                            <span>Priority</span>
                            <select
                                aria-label={`Priority for ${task.title}`}
                                value={priorityKey(task.priority)}
                                onChange={(event) => onUpdate(task.id, { priority: event.target.value })}
                            >
                                {PRIORITIES.map((priority) => <option key={priority.key} value={priority.key}>{priority.label}</option>)}
                            </select>
                        </label>
                        <button type="button" className="task-status-button" onClick={() => onUpdate(task.id, { status: done ? 'open' : 'done' })}>
                            {done ? 'Reopen' : 'Mark done'}
                        </button>
                    </>
                )}
                <button type="button" className="task-comments-toggle" onClick={() => setCommentsOpen((open) => !open)}>
                    {commentsOpen ? 'Hide comments' : `Comments ${task.comments?.length || 0}`}
                </button>
            </div>

            {commentsOpen && (
                <div className="task-comments">
                    {topLevelComments.length ? topLevelComments.map((entry) => {
                        const replies = repliesFor(entry.id);
                        return (
                            <div className="task-comment-thread" key={entry.id}>
                                {renderComment(entry, entry.id)}
                                {replies.length > 0 && (
                                    <div className="task-comment-replies">
                                        {replies.map((reply) => renderComment(reply, entry.id))}
                                    </div>
                                )}
                                {replyThreadId === entry.id && (
                                    <form className="task-comment-form task-reply-form" onSubmit={submitReply}>
                                        <input
                                            autoFocus
                                            aria-label={`Reply to ${displayName(entry.author)}`}
                                            value={replyDraft}
                                            onChange={(event) => setReplyDraft(event.target.value)}
                                            placeholder="Write a reply"
                                            maxLength={2000}
                                        />
                                        <button type="submit" disabled={replyBusy || !replyDraft.trim()} aria-label="Post reply">Reply</button>
                                        <button type="button" className="task-reply-cancel" onClick={() => { setReplyThreadId(null); setReplyDraft(''); }}>Cancel</button>
                                    </form>
                                )}
                            </div>
                        );
                    }) : <p className="task-no-comments">No comments yet.</p>}
                    {task.canWrite && (
                        <form className="task-comment-form" onSubmit={submitComment}>
                            <input
                                aria-label="Write a comment"
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                placeholder="Add a comment"
                                maxLength={2000}
                            />
                            <button type="submit" disabled={commentBusy || !comment.trim()} aria-label="Post comment">Post</button>
                        </form>
                    )}
                </div>
            )}
        </article>
    );
};

const TasksPage = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [properties, setProperties] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDone, setShowDone] = useState(false);
    const [assigneeFilter, setAssigneeFilter] = useState('all');
    const [grouping, setGrouping] = useState('assignee');
    const [composerOpen, setComposerOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rentTasksBusy, setRentTasksBusy] = useState(false);
    const [error, setError] = useState('');
    const [rentTaskMessage, setRentTaskMessage] = useState('');
    const [form, setForm] = useState({ propertyId: '', taskFunction: 'support', title: '', description: '', priority: 'medium', assigneeUserId: '', deadlineDate: '', deadlineTime: DEFAULT_DEADLINE_TIME });

    const loadBoard = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get(`/task-board?showDone=${showDone}`);
            const payload = response?.data || {};
            const nextProperties = Array.isArray(payload.properties) ? payload.properties : [];
            setProperties(nextProperties);
            setTasks(Array.isArray(payload.tasks) ? payload.tasks : []);
            setForm((current) => {
                const selectedExists = current.propertyId === 'personal'
                    || nextProperties.some((property) => String(property.id) === String(current.propertyId) && property.canWrite);
                const defaultProperty = nextProperties.find((property) => property.canWrite);
                return {
                    ...current,
                    propertyId: selectedExists ? current.propertyId : String(defaultProperty?.id || 'personal'),
                    assigneeUserId: selectedExists ? current.assigneeUserId : ''
                };
            });
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to load tasks.');
        } finally {
            setLoading(false);
        }
    }, [showDone]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        if (String(user.role || '').toLowerCase() === 'guest') {
            navigate('/', { replace: true });
            return;
        }
        loadBoard();
    }, [user, navigate, loadBoard]);

    const assignees = useMemo(() => {
        const people = new Map();
        properties.forEach((property) => property.hosts?.forEach((person) => people.set(Number(person.id), person)));
        return Array.from(people.values()).sort((left, right) => displayName(left).localeCompare(displayName(right)));
    }, [properties]);

    const visibleTasks = tasks.filter((task) => assigneeFilter === 'all' || String(task.assigneeUserId || '') === assigneeFilter);
    const openTasks = visibleTasks.filter((task) => !isDone(task));
    const doneTasks = visibleTasks.filter(isDone);
    const writableProperties = properties.filter((property) => property.canWrite);
    const ownedProperties = properties.filter((property) => property.isOwned);
    const isPersonalTask = form.propertyId === 'personal';
    const selectedProperty = properties.find((property) => String(property.id) === String(form.propertyId));
    const writableTaskCount = tasks.filter((task) => task.canWrite && !isDone(task)).length;
    const legendPeople = Array.from(new Map(
        visibleTasks.filter((task) => task.assignee).map((task) => [Number(task.assignee.id), task.assignee])
    ).values()).sort((left, right) => displayName(left).localeCompare(displayName(right)));
    const taskGroups = useMemo(() => {
        if (grouping === 'assignee') {
            const currentUserId = Number(user?.id);
            const people = new Map(assignees.map((person) => [Number(person.id), person]));
            visibleTasks.forEach((task) => {
                if (task.assignee) people.set(Number(task.assignee.id), task.assignee);
            });
            if (currentUserId > 0 && !people.has(currentUserId)) {
                people.set(currentUserId, { id: currentUserId, display: user.display, email: user.email });
            }
            const groups = Array.from(people.values())
                .filter((person) => assigneeFilter === 'all' || String(person.id) === assigneeFilter)
                .sort((left, right) =>
                    Number(Number(right.id) === currentUserId) - Number(Number(left.id) === currentUserId)
                    || displayName(left).localeCompare(displayName(right)))
                .map((person) => ({
                    key: `assignee:${person.id}`,
                    label: Number(person.id) === currentUserId ? `${displayName(person)} (you)` : displayName(person),
                    colorId: Number(person.id),
                    tasks: visibleTasks.filter((task) => Number(task.assigneeUserId) === Number(person.id))
                }));
            const unassigned = visibleTasks.filter((task) => !task.assigneeUserId);
            return unassigned.length
                ? [...groups, { key: 'assignee:none', label: 'Unassigned', colorId: null, tasks: unassigned }]
                : groups;
        }

        if (grouping === 'function') {
            return TASK_FUNCTIONS.map((taskFunction) => ({
                key: `function:${taskFunction.key}`,
                label: taskFunction.label,
                tasks: visibleTasks.filter((task) => taskFunctionKey(task.taskFunction) === taskFunction.key)
            }));
        }

        const personalTasks = visibleTasks.filter((task) => task.propertyId == null);
        const propertyGroups = properties.map((property) => ({
            key: `property:${property.id}`,
            label: property.name || 'Property',
            tasks: visibleTasks.filter((task) => Number(task.propertyId) === Number(property.id))
        })).filter((group) => group.tasks.length > 0);
        return [
            ...(personalTasks.length ? [{ key: 'personal', label: 'Personal', tasks: personalTasks }] : []),
            ...propertyGroups
        ];
    }, [grouping, properties, visibleTasks, assignees, assigneeFilter, user]);
    const openTaskGroups = taskGroups.map((group) => ({ ...group, tasks: group.tasks.filter((task) => !isDone(task)) }));
    const doneTaskGroups = taskGroups.map((group) => ({ ...group, tasks: group.tasks.filter(isDone) }));

    const updateTask = async (taskId, patch) => {
        setError('');
        try {
            await api.put(`/task-board/tasks/${taskId}`, patch);
            await loadBoard();
            return true;
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to update task.');
            return false;
        }
    };

    const addComment = async (taskId, body, parentCommentId = null) => {
        setError('');
        try {
            const response = await api.post(`/task-board/tasks/${taskId}/comments`, { body, parentCommentId });
            setTasks((current) => current.map((task) => task.id === taskId
                ? { ...task, comments: [...(task.comments || []), response.data] }
                : task));
            return true;
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to add comment.');
            return false;
        }
    };

    const likeComment = async (taskId, commentId, liked) => {
        setError('');
        try {
            const response = await api.put(`/task-board/tasks/${taskId}/comments/${commentId}/like`, { liked });
            setTasks((current) => current.map((task) => task.id === taskId
                ? { ...task, comments: (task.comments || []).map((entry) => (entry.id === commentId ? { ...entry, ...response.data } : entry)) }
                : task));
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to update like.');
        }
    };

    const createTask = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        try {
            await api.post('/task-board/tasks', {
                ...form,
                propertyId: isPersonalTask ? null : Number(form.propertyId),
                assigneeUserId: isPersonalTask ? Number(user.id) : form.assigneeUserId ? Number(form.assigneeUserId) : null,
                deadline: form.deadlineDate
                    ? new Date(`${form.deadlineDate}T${form.deadlineTime || DEFAULT_DEADLINE_TIME}`).toISOString()
                    : null
            });
            setForm((current) => ({ ...current, title: '', description: '', assigneeUserId: '', deadlineDate: '', deadlineTime: DEFAULT_DEADLINE_TIME }));
            setComposerOpen(false);
            await loadBoard();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to create task.');
        } finally {
            setSaving(false);
        }
    };

    const createRentTasks = async () => {
        const propertyCount = ownedProperties.length;
        if (propertyCount === 0 || rentTasksBusy) return;
        const confirmed = window.confirm(
            `Create a Finance task to pay rent for each of your ${propertyCount} active ${propertyCount === 1 ? 'property' : 'properties'}? Existing open rent tasks will be left unchanged.`
        );
        if (!confirmed) return;

        setRentTasksBusy(true);
        setError('');
        setRentTaskMessage('');
        try {
            const response = await api.post('/task-board/tasks/rent-batch', {});
            const { createdCount = 0, skippedCount = 0 } = response?.data || {};
            setRentTaskMessage(
                createdCount === 0
                    ? `No new rent tasks created. ${skippedCount} ${skippedCount === 1 ? 'property already has' : 'properties already have'} an open rent task.`
                    : `Created ${createdCount} rent ${createdCount === 1 ? 'task' : 'tasks'}${skippedCount ? `; skipped ${skippedCount} with an existing open rent task` : ''}.`
            );
            await loadBoard();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to create rent tasks.');
        } finally {
            setRentTasksBusy(false);
        }
    };

    const reorderTasks = async (draggedId, targetId, priority, groupKey) => {
        const group = openTasks.filter((task) =>
            priorityKey(task.priority) === priority && taskGroupKey(task, grouping) === groupKey
        );
        if (group.some((task) => !task.canWrite) || assigneeFilter !== 'all') return;
        const ordered = [...group];
        const fromIndex = ordered.findIndex((task) => String(task.id) === String(draggedId));
        const toIndex = ordered.findIndex((task) => String(task.id) === String(targetId));
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
        const [moved] = ordered.splice(fromIndex, 1);
        ordered.splice(toIndex, 0, moved);
        setTasks((current) => current.map((task) => {
            const index = ordered.findIndex((entry) => Number(entry.id) === Number(task.id));
            return index < 0 ? task : { ...task, boardOrder: index };
        }));
        try {
            await api.put('/task-board/tasks/reorder', { taskIds: ordered.map((task) => Number(task.id)) });
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to save task order.');
            await loadBoard();
        }
    };

    const renderTaskGroups = (groups, completed = false) => (
        <div className={`task-groups-board${completed ? ' task-groups-completed' : ''}`}>
            {groups.map((group) => (
                <section className="task-group" key={group.key} style={group.colorId !== undefined ? assigneeColorStyle(group.colorId) : undefined}>
                    <header className="task-group-header">
                        <h2>{group.colorId !== undefined && <span className="task-group-dot" aria-hidden="true" />}{group.label}</h2>
                        <span>{group.tasks.length}</span>
                    </header>
                    <div className="task-group-list">
                        {group.tasks.map((task) => {
                            const samePriorityTasks = group.tasks.filter((entry) => priorityKey(entry.priority) === priorityKey(task.priority));
                            const canDrag = !completed
                                && assigneeFilter === 'all'
                                && samePriorityTasks.length > 1
                                && samePriorityTasks.every((entry) => entry.canWrite);
                            return (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    properties={properties}
                                    canDrag={canDrag}
                                    groupKey={group.key}
                                    onUpdate={updateTask}
                                    onDrop={reorderTasks}
                                    onComment={addComment}
                                    onLike={likeComment}
                                />
                            );
                        })}
                        {group.tasks.length === 0 && <p className="task-group-empty">No tasks</p>}
                    </div>
                </section>
            ))}
        </div>
    );

    if (!user || String(user.role || '').toLowerCase() === 'guest') return null;

    return (
        <main className="task-board-page">
            <header className="task-board-header">
                <div>
                    <p className="task-board-eyebrow">Shared property work</p>
                    <h1>Tasks</h1>
                    <p className="task-board-summary">{openTasks.length} open {openTasks.length === 1 ? 'task' : 'tasks'} on your board</p>
                </div>
                <div className="task-board-header-actions">
                    {ownedProperties.length > 0 && (
                        <button type="button" className="task-rent-button" onClick={createRentTasks} disabled={rentTasksBusy}>
                            {rentTasksBusy ? 'Creating rent tasks...' : 'Create rent tasks'}
                        </button>
                    )}
                    <button type="button" className="task-new-button" onClick={() => setComposerOpen((open) => !open)}>
                        <span aria-hidden="true">+</span> New task
                    </button>
                </div>
            </header>

            <div className="task-board-toolbar">
                <div className="task-view-switch" role="group" aria-label="Group tasks by">
                    <button type="button" aria-pressed={grouping === 'assignee'} onClick={() => setGrouping('assignee')}>Assignee</button>
                    <button type="button" aria-pressed={grouping === 'function'} onClick={() => setGrouping('function')}>Function</button>
                    <button type="button" aria-pressed={grouping === 'property'} onClick={() => setGrouping('property')}>Property</button>
                </div>
                <label className="task-filter-label">
                    Assignee
                    <select value={assigneeFilter} onChange={(event) => setAssigneeFilter(event.target.value)}>
                        <option value="all">All hosts</option>
                        {assignees.map((person) => <option key={person.id} value={person.id}>{displayName(person)}</option>)}
                    </select>
                </label>
                <label className="task-done-toggle">
                    <input type="checkbox" checked={showDone} onChange={(event) => setShowDone(event.target.checked)} />
                    <span>Show completed</span>
                </label>
                <span className="task-board-hint">{writableTaskCount} tasks you can update</span>
            </div>

            {grouping !== 'assignee' && legendPeople.length > 0 && (
                <div className="task-assignee-legend" aria-label="Assignee colors">
                    {legendPeople.map((person) => (
                        <span key={person.id} style={assigneeColorStyle(person.id)}><i aria-hidden="true" />{displayName(person)}</span>
                    ))}
                </div>
            )}

            {error && <div className="task-board-error" role="alert">{error}</div>}
            {rentTaskMessage && <div className="task-board-success" role="status">{rentTaskMessage}</div>}

            {composerOpen && (
                <form className="task-composer" onSubmit={createTask}>
                    <div className="task-composer-heading">
                        <h2>Create a task</h2>
                        <button type="button" className="task-close-button" onClick={() => setComposerOpen(false)} aria-label="Close task form">×</button>
                    </div>
                    <div className="task-composer-grid">
                        <label>
                            Task space
                            <select required value={form.propertyId} onChange={(event) => setForm((current) => ({ ...current, propertyId: event.target.value, assigneeUserId: '' }))}>
                                <option value="personal">Personal</option>
                                {writableProperties.map((property) => <option key={property.id} value={property.id}>{property.name}</option>)}
                            </select>
                        </label>
                        <label>
                            Function
                            <select required value={form.taskFunction} onChange={(event) => setForm((current) => ({ ...current, taskFunction: event.target.value }))}>
                                {TASK_FUNCTIONS.map((taskFunction) => <option key={taskFunction.key} value={taskFunction.key}>{taskFunction.label}</option>)}
                            </select>
                        </label>
                        <label>
                            Priority
                            <select value={form.priority} onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}>
                                {PRIORITIES.map((priority) => <option key={priority.key} value={priority.key}>{priority.label}</option>)}
                            </select>
                        </label>
                        <label>
                            Assign to
                            {isPersonalTask ? <span className="task-composer-assignee">You (personal tasks are private)</span> : (
                                <select value={form.assigneeUserId} onChange={(event) => setForm((current) => ({ ...current, assigneeUserId: event.target.value }))}>
                                    <option value="">Unassigned</option>
                                    {(selectedProperty?.hosts || []).map((person) => <option key={person.id} value={person.id}>{displayName(person)}</option>)}
                                </select>
                            )}
                        </label>
                    </div>
                    <label className="task-composer-title">
                        Task
                        <input required autoFocus maxLength={255} value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="What needs to get done?" />
                    </label>
                    <label className="task-composer-description">
                        Details
                        <textarea rows="3" maxLength={4000} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Add context for the host taking this on" />
                    </label>
                    <div className="task-composer-deadline">
                        <span>Deadline <em>(optional; time defaults to 4:00 PM)</em></span>
                        <div className="task-deadline-fields">
                            <input type="date" aria-label="Deadline date" value={form.deadlineDate} onChange={(event) => setForm((current) => ({ ...current, deadlineDate: event.target.value }))} />
                            <input type="time" aria-label="Deadline time" value={form.deadlineTime} disabled={!form.deadlineDate} onChange={(event) => setForm((current) => ({ ...current, deadlineTime: event.target.value }))} />
                            {form.deadlineDate && (
                                <button type="button" className="task-deadline-clear" onClick={() => setForm((current) => ({ ...current, deadlineDate: '', deadlineTime: DEFAULT_DEADLINE_TIME }))}>Clear</button>
                            )}
                        </div>
                    </div>
                    <div className="task-composer-footer">
                        <button type="button" className="task-cancel-button" onClick={() => setComposerOpen(false)}>Cancel</button>
                        <button type="submit" className="task-save-button" disabled={saving || (!isPersonalTask && !selectedProperty?.canWrite)}>{saving ? 'Creating...' : 'Create task'}</button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="task-board-empty">Loading tasks...</div>
            ) : (
                <>
                    {properties.length === 0 && <p className="task-personal-note">Personal tasks are private to your account. Add a shared property to collaborate with other hosts.</p>}
                    {grouping === 'property' && taskGroups.length === 0 ? (
                        <div className="task-board-empty">No tasks in this view.</div>
                    ) : renderTaskGroups(openTaskGroups)}
                    {showDone && (
                        <section className="task-completed-section">
                            <header><div><p>Archive</p><h2>Completed tasks</h2></div><span>{doneTasks.length}</span></header>
                            {doneTasks.length ? (
                                renderTaskGroups(doneTaskGroups, true)
                            ) : <p className="task-lane-empty">No completed tasks match this filter.</p>}
                        </section>
                    )}
                </>
            )}
        </main>
    );
};

export default TasksPage;